/**
 * Identity transport boundary — users, roles and the permission catalogue.
 *
 * Concurrency policy, verified against openapi/generated/route_policy.gen.go:
 *   - POST /users and POST /roles are `client-key` idempotent and MUST carry
 *     an Idempotency-Key.
 *   - Every PATCH and every set-replacing PUT takes a REQUIRED strong If-Match.
 *   - Nothing is deleted: a user is disabled, a role is archived.
 *
 * A password is sent and never received. No function here returns one, and no
 * screen keeps one after the request that carried it.
 */
import type { components } from "@erp/api-client";

import { apiFetch, apiRequest, type Page } from "@/lib/api/http";
import { collectCursorPages, fetchWithEtag, type WithEtag } from "@/features/shared/ops-api";

type Schemas = components["schemas"];

export type User = Schemas["PlatformUser"];
export type UserCreate = Schemas["PlatformUserCreate"];
export type UserPatch = Schemas["PlatformUserPatch"];
export type UserStatus = Schemas["PlatformUserStatus"];
export type Role = Schemas["PlatformRole"];
export type RoleCreate = Schemas["PlatformRoleCreate"];
export type RolePatch = Schemas["PlatformRolePatch"];
export type RoleStatus = Schemas["PlatformRoleStatus"];
export type Permission = Schemas["PlatformPermission"];

const MERGE_PATCH = { "Content-Type": "application/merge-patch+json" } as const;

// Identity is company-scoped, never branch-scoped: an administrator manages the
// same people whichever branch they happen to be acting in.
export function listUsers(query: { status?: UserStatus; q?: string } = {}, signal?: AbortSignal) {
  return collectCursorPages(
    (cursor, pageSignal) =>
      apiFetch<Page<User>>("/users", {
        query: { ...query, limit: 200, ...(cursor === undefined ? {} : { cursor }) },
        signal: pageSignal,
      }),
    { signal },
  );
}

export function getUser(userId: string, signal?: AbortSignal): Promise<WithEtag<User>> {
  return fetchWithEtag<User>(`/users/${encodeURIComponent(userId)}`, { signal });
}

export function createUser(input: UserCreate, idempotencyKey: string): Promise<User> {
  return apiFetch<User>("/users", { method: "POST", body: input, idempotencyKey });
}

export async function updateUser(
  userId: string,
  patch: UserPatch,
  ifMatch: string,
): Promise<WithEtag<User>> {
  const { data, etag } = await apiRequest<User>(`/users/${encodeURIComponent(userId)}`, {
    method: "PATCH",
    body: patch,
    ifMatch,
    headers: { ...MERGE_PATCH },
  });
  return { data, etag };
}

export async function setUserPassword(
  userId: string,
  password: string,
  ifMatch: string,
): Promise<void> {
  await apiRequest<void>(`/users/${encodeURIComponent(userId)}/password`, {
    method: "PUT",
    body: { password },
    ifMatch,
  });
}

export async function replaceUserRoles(
  userId: string,
  roleIds: string[],
  ifMatch: string,
): Promise<WithEtag<User>> {
  const { data, etag } = await apiRequest<User>(`/users/${encodeURIComponent(userId)}/roles`, {
    method: "PUT",
    body: { role_ids: roleIds },
    ifMatch,
  });
  return { data, etag };
}

export async function replaceUserBranches(
  userId: string,
  branchIds: string[],
  ifMatch: string,
): Promise<WithEtag<User>> {
  const { data, etag } = await apiRequest<User>(`/users/${encodeURIComponent(userId)}/branches`, {
    method: "PUT",
    body: { branch_ids: branchIds },
    ifMatch,
  });
  return { data, etag };
}

export function listRoles(query: { status?: RoleStatus } = {}, signal?: AbortSignal) {
  return collectCursorPages(
    (cursor, pageSignal) =>
      apiFetch<Page<Role>>("/roles", {
        query: { ...query, limit: 200, ...(cursor === undefined ? {} : { cursor }) },
        signal: pageSignal,
      }),
    { signal },
  );
}

export function getRole(roleId: string, signal?: AbortSignal): Promise<WithEtag<Role>> {
  return fetchWithEtag<Role>(`/roles/${encodeURIComponent(roleId)}`, { signal });
}

export function createRole(input: RoleCreate, idempotencyKey: string): Promise<Role> {
  return apiFetch<Role>("/roles", { method: "POST", body: input, idempotencyKey });
}

export async function updateRole(
  roleId: string,
  patch: RolePatch,
  ifMatch: string,
): Promise<WithEtag<Role>> {
  const { data, etag } = await apiRequest<Role>(`/roles/${encodeURIComponent(roleId)}`, {
    method: "PATCH",
    body: patch,
    ifMatch,
    headers: { ...MERGE_PATCH },
  });
  return { data, etag };
}

export async function replaceRolePermissions(
  roleId: string,
  permissionCodes: string[],
  ifMatch: string,
): Promise<WithEtag<Role>> {
  const { data, etag } = await apiRequest<Role>(
    `/roles/${encodeURIComponent(roleId)}/permissions`,
    { method: "PUT", body: { permission_codes: permissionCodes }, ifMatch },
  );
  return { data, etag };
}

/** The permission catalogue is a few hundred codes and is drained whole: the
 *  role editor groups them by domain, which it cannot do a page at a time. */
export function listPermissions(signal?: AbortSignal) {
  return collectCursorPages(
    (cursor, pageSignal) =>
      apiFetch<Page<Permission>>("/permissions", {
        query: { limit: 200, ...(cursor === undefined ? {} : { cursor }) },
        signal: pageSignal,
      }),
    { signal },
  );
}
